import { type PaymentRequestsCreationResult, type SinglePaymentRequest, useUploadPaymentRequestsJSON } from 'api';
import { useTranslation } from 'i18n';
import { addError } from 'monitor';
import Papa from 'papaparse';
import { type ChangeEvent, useMemo, useState } from 'react';
import { Alert, Box, Button, DataGrid, FilePicker, type GridColDef, Typography } from 'ui';

import { OutputDataGridWithDownload } from '../OutputDataGridWithDownload/OutputDataGridWithDownload';
import { ProcessingErrors } from '../ProcessingErrors/ProcessingErrors';

interface GridData {
  rows: Array<SinglePaymentRequest>;
  columns: Array<GridColDef>;
}

export const SubmitPaymentRequests = () => {
  const { t } = useTranslation('orders');
  const { mutate, isPending: processing, data, error, isSuccess } = useUploadPaymentRequestsJSON();
  const [file, setFile] = useState<File | null>(null);
  const [filePickerError, setFilePickerError] = useState<string | null>(null);
  const [inputGridData, setInputGridData] = useState<GridData | null>(null);

  const outputGridData = useMemo(() => {
    if (!data || !data.results) {
      return null;
    }
    const responseHeader = Object.keys(data.results[0]);
    return {
      columns: responseHeader.map((field) => ({
        field,
        flex: 1,
        headerName: field,
      })),
      rows: data.results,
    };
  }, [data]);

  const loadCsvContent = (csv: string) => {
    const parsed = Papa.parse<SinglePaymentRequest>(csv, {
      header: true,
      skipEmptyLines: true,
    });
    if (!parsed.meta.fields) {
      return;
    }
    if (!parsed.meta.fields.find((field) => field === 'buyer_id')) {
      setFilePickerError(t('financing.submitPaymentRequests.errors.csvMustContainBuyerId'));
      return;
    }
    setInputGridData({
      columns: parsed.meta.fields.map((field) => ({
        field,
        flex: 1,
        headerName: field,
      })),
      rows: parsed.data,
    });
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList?.length) {
      setFile(fileList[0]);
      const reader = new FileReader();
      reader.onload = (e) => {
        const csv = e.target?.result;
        if (typeof csv !== 'string') {
          addError(Error('SubmitPaymentRequests invalid csv content'), {
            csv,
            fileList,
          });
          return;
        }
        loadCsvContent(csv);
      };
      reader.readAsText(fileList[0]);
    } else {
      setInputGridData(null);
      setFile(null);
    }
    setFilePickerError(null);
  };

  const handleCreatePaymentRequests = async () => {
    const data = inputGridData?.rows;
    if (!data) {
      return;
    }
    await mutate({
      data: data.map((row) => ({
        buyer_id: row.buyer_id,
        payment_currency: row.payment_currency,
        payment_id: row.payment_id,
        payment_invoice_due_date: row.payment_invoice_due_date,
        payment_invoice_number: row.payment_invoice_number,
        payment_total_amount: row.payment_total_amount,
        repayment_term_days: row.repayment_term_days,
      })),
    });
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{t('financing.submitPaymentRequests.title')}</Typography>
      </Box>
      <Box sx={{ mb: 2 }}>
        <FilePicker
          accept=".csv"
          onChange={handleFileChange}
          label={t('financing.submitPaymentRequests.labels.pickAFile')}
          showSelectedFileName={true}
          error={filePickerError}
        />
      </Box>

      {inputGridData ? (
        <Box sx={{ mb: 2 }}>
          <DataGrid<SinglePaymentRequest>
            rows={inputGridData.rows}
            columns={inputGridData.columns}
            hideFooter={true}
            getRowId={(row) => row.buyer_id}
          />
        </Box>
      ) : null}

      {isSuccess ? (
        <Alert severity="success">{t('financing.submitPaymentRequests.notifications.submittedSuccessfully')}</Alert>
      ) : null}
      {
        // @ts-expect-error incomplete type
        error?.response?.data.detail ? <Alert severity="error">{error?.response?.data.detail}</Alert> : null
      }
      {
        // @ts-expect-error incomplete type
        error?.response?.data.row_errors ? <ProcessingErrors errors={error?.response?.data.row_errors} /> : null
      }

      {outputGridData ? (
        <Box sx={{ my: 2 }}>
          <OutputDataGridWithDownload<PaymentRequestsCreationResult>
            rows={outputGridData.rows}
            columns={outputGridData.columns}
            rowIdGetter={(row) => `${row.buyer_id}`}
            filenamePrefix={'payment-requests-submission-result'}
          />
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', mt: 2 }}>
        <Button onClick={handleCreatePaymentRequests} disabled={!file || processing} loading={processing}>
          {t('financing.submitPaymentRequests.actions.submitPaymentsToFinmid')}
        </Button>
      </Box>
    </Box>
  );
};
